import { useEffect, useState } from 'react'
import { CalendarCheck, CalendarX, CheckCircle2, Clock, Video } from 'lucide-react'
import {
  appointmentsApi,
  type Appointment,
} from '@/services/appointmentsApi'
import ConfirmAppointmentModal from '@/pages/appointments/components/ConfirmAppointmentModal'
import CancelAppointmentModal from '@/pages/appointments/components/CancelAppointmentModal'
import { useVerticalUI } from '@/lib/verticalUI'

interface AppointmentRequestCardProps {
  conversationId: string
}

function formatSlot(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-VE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

interface AppointmentRequestCardViewProps {
  appointment: Appointment
  onConfirm: () => void
  onReject: () => void
}

/**
 * Vista presentacional del widget (fase A4): PENDING → alerta con acciones
 * manuales; CONFIRMED → tarjeta de confirmación. Separada del wrapper de
 * datos para ser testeable en SSR.
 */
export function AppointmentRequestCardView({
  appointment,
  onConfirm,
  onReject,
}: AppointmentRequestCardViewProps) {
  const { profile } = useVerticalUI()
  const appointmentSingular =
    profile.nav.showAppointments && !profile.nav.showOrders
      ? profile.terminology.primaryTransactionSingular
      : 'Cita'

  if (appointment.status === 'PENDING') {
    return (
      <div
        data-testid="appointment-request-card"
        role="region"
        aria-label={`Solicitud de ${appointmentSingular.toLowerCase()} pendiente`}
        className="mt-2.5 rounded-xl border border-amber-300/80 bg-amber-50/90 p-3 shadow-2xs"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <Clock className="h-4 w-4" />
            <span>📅 Solicitud de {appointmentSingular} Pendiente</span>
          </div>
          <span className="rounded-full bg-amber-200/80 px-2 py-0.5 text-xs font-medium text-amber-900">
            requiere confirmación manual
          </span>
        </div>

        <div className="mt-2 text-xs text-amber-900/80">
          <p>
            <span className="font-semibold">{profile.terminology.itemSingularName}:</span>{' '}
            {appointment.serviceName ?? 'Consulta'} ·{' '}
            {appointment.modality === 'REMOTE' ? (
              <span className="inline-flex items-center gap-1">
                <Video className="h-3 w-3" /> Telemedicina
              </span>
            ) : (
              'Presencial 🏥'
            )}
          </p>
          <p>
            <span className="font-semibold">Pidió:</span> “{appointment.requestedSlotText}”
          </p>
        </div>

        <div className="mt-2.5 flex flex-wrap gap-2">
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            <CalendarCheck className="h-3.5 w-3.5" />
            Confirmar Slot Definitivo
          </button>
          <button
            onClick={onReject}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
          >
            <CalendarX className="h-3.5 w-3.5" />
            Rechazar
          </button>
        </div>
      </div>
    )
  }

  if (appointment.status === 'CONFIRMED') {
    return (
      <div
        data-testid="appointment-confirmed-card"
        role="region"
        aria-label={`${appointmentSingular} confirmada`}
        className="mt-2.5 rounded-xl border border-emerald-200/80 bg-emerald-50/90 p-3 shadow-2xs"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
          <CheckCircle2 className="h-4 w-4" />
          <span>✅ {appointmentSingular} Confirmada</span>
        </div>
        <p className="mt-1.5 text-xs text-emerald-900/80">
          {formatSlot(appointment.confirmedSlotStart)}
          {appointment.locationOrRoom ? ` · ${appointment.locationOrRoom}` : ''}
        </p>
      </div>
    )
  }

  return null
}

/**
 * Widget embebido en el inbox de chat (fase A4, plan citas).
 *
 * - Cita PENDING → alerta "Solicitud de Cita Pendiente" con acciones
 *   [Confirmar Slot Definitivo] | [Rechazar] SIN abandonar la conversación.
 * - Cita CONFIRMED → tarjeta de confirmación con fecha y consultorio.
 *
 * El bot NUNCA confirma: los botones invocan la acción manual del staff.
 */
export function AppointmentRequestCard({ conversationId }: AppointmentRequestCardProps) {
  const [appointment, setAppointment] = useState<Appointment | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)

  const loadAppointment = () => {
    if (!conversationId) return
    appointmentsApi
      .getAppointments({ conversationId, limit: 1 })
      .then((result) => {
        const active = result.appointments.find(
          (a) => a.status === 'PENDING' || a.status === 'CONFIRMED'
        )
        setAppointment(active ?? null)
      })
      .catch(() => setAppointment(null))
      .finally(() => setLoaded(true))
  }

  useEffect(loadAppointment, [conversationId])

  if (!loaded || !appointment) return null

  return (
    <>
      <AppointmentRequestCardView
        appointment={appointment}
        onConfirm={() => setConfirmOpen(true)}
        onReject={() => setCancelOpen(true)}
      />

      {appointment.status === 'PENDING' && confirmOpen && (
        <ConfirmAppointmentModal
          appointment={appointment}
          onClose={() => setConfirmOpen(false)}
          onConfirmed={() => {
            setConfirmOpen(false)
            loadAppointment()
          }}
        />
      )}
      {appointment.status === 'PENDING' && cancelOpen && (
        <CancelAppointmentModal
          appointment={appointment}
          onClose={() => setCancelOpen(false)}
          onCancelled={() => {
            setCancelOpen(false)
            loadAppointment()
          }}
        />
      )}
    </>
  )
}
